import React from 'react';
import classnames from 'classnames';

// Hooks
import useElementDimensions from 'Wpds/hooks/useElementDimensions';

// Types
import { TypeNames, InsightType, SurfaceFilters } from 'Shared/types/Capture.types';

// Utils
import { getSurfaceTypeName, getNormalizedInsight } from './SurfaceAI.utils';

// Components
import SurfaceAITableLoading from './SurfaceAITableLoading';
import SurfaceAITableItem from './SurfaceAITableItem';
import SurfaceAITableHeaders from './SurfaceAITableHeaders';

// Styles
import './SurfaceAITable.scss';

const DEFAULT_FILTERS = {
  name: '',
  typeName: [],
  insight: [],
  status: [],
  users: [],
};

export enum SurfaceAITableViewType {
  TABLE = 'table',
  LIST = 'list',
}

type Props = {
  surfaceData;
  loading: boolean;
  showTableHeaders?: boolean;
  bordered?: boolean;
  surfaceItemActions?: Function;
  filters?: SurfaceFilters;
  parentDiv?: React.Ref<HTMLDivElement>;
};

const SurfaceAITable = ({
  surfaceData,
  loading,
  showTableHeaders = true,
  bordered = true,
  surfaceItemActions,
  filters = DEFAULT_FILTERS,
  parentDiv,
}: Props) => {
  const { width: elementWidth } = useElementDimensions({ elementRef: parentDiv || null });
  const normalizedFilters = { ...DEFAULT_FILTERS, ...filters };

  const showListView = (elementWidth && elementWidth < 768) || false;

  const getFilteredData = () => {
    let items = surfaceData;

    const nameFilters = normalizedFilters.name;
    const typeNameFilters = normalizedFilters.typeName;
    const insightFilters = normalizedFilters.insight;
    const statusFilters = normalizedFilters.status;
    const usersFilters = normalizedFilters.users;

    // filter on name
    if (nameFilters !== '') {
      items = items.filter(item => item.name.toLowerCase().includes(nameFilters.toLowerCase()));
    }

    // filter on integration type
    if (typeNameFilters.length > 0) {
      items = items.filter(item => {
        const normalizedType = getSurfaceTypeName({ type: item.type, subtype: item.subtype }) as TypeNames;
        return filters.typeName.includes(normalizedType);
      });
    }

    // filter on insight
    if (insightFilters.length > 0) {
      items = items.filter(item => {
        const insight = getNormalizedInsight({ surfaceItem: item });

        // check for blocked
        if (insight === InsightType.BLOCKED && insightFilters.includes(InsightType.BLOCKED)) {
          return true;
        }

        // check for active
        if (insight === InsightType.ACTIVE && insightFilters.includes(InsightType.ACTIVE)) {
          return true;
        }

        // check for new
        if (insight === InsightType.NEW && insightFilters.includes(InsightType.NEW)) {
          return true;
        }
      });
    }

    // filter on status
    if (statusFilters.length > 0) {
      items = items.filter(item => statusFilters.includes(item.status));
    }

    // filter on users
    if (usersFilters.length > 0) {
      items = items.filter(item => {
        const userIdsOnItem = item.user_ids;
        for (let i = 0; i < userIdsOnItem.length; i += 1) {
          if (usersFilters.indexOf(userIdsOnItem[i]) > -1) {
            return true;
          }
        }
      });
    }

    return items;
  };
  const showEmpty = !loading && surfaceData && surfaceData.length === 0;
  const hasData = !loading && surfaceData && surfaceData.length > 0;
  const itemsToRender = hasData && getFilteredData();
  const baseClassName = 'SurfaceAITable';
  const classes = classnames(baseClassName, bordered ? `${baseClassName}--bordered` : false);
  return (
    <div className={classes}>
      {showTableHeaders && !showListView && <SurfaceAITableHeaders />}
      {loading && <SurfaceAITableLoading />}
      {showEmpty && <div className={`${baseClassName}__empty`}>No SurfaceAI items found</div>}
      {hasData && itemsToRender.length === 0 && (
        <div className={`${baseClassName}__empty`}>No SurfaceAI items found matching your filters.</div>
      )}
      {itemsToRender.length > 0 &&
        itemsToRender.map(surfaceItem => (
          <SurfaceAITableItem
            surfaceItem={surfaceItem}
            key={`surface-item-${surfaceItem.type}-${surfaceItem.id}`}
            actions={surfaceItemActions}
            viewType={showListView ? SurfaceAITableViewType.LIST : SurfaceAITableViewType.TABLE}
          />
        ))}
    </div>
  );
};

export default SurfaceAITable;
